import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import * as companiesApi from "../api"

//Tiempo de vida de la lista de compañías en el store (30 minutos)
export const COMPANIES_CACHE_TTL = 30 * 60 * 1000
//Las listas filtradas (seguimiento y buzón tributario) cambian al dar de alta un trámite, de ahí su ventana corta
export const COMPANIES_FILTERED_CACHE_TTL = 60 * 1000

const asList = (data) => (Array.isArray(data) ? data : [])

//Redux Toolkit rellena `action.error.message` con este literal cuando el thunk se rechaza con
//`rejectWithValue`, que es justo el caso de una caída de red: sin respuesta HTTP no hay
//`error.response.data` que pasarle
const RTK_REJECTED_SENTINEL = 'Rejected'

//`error` guarda lo que dijo el backend y nada más. Un fallo sin respuesta se guarda como null en
//vez del literal en inglés de Redux Toolkit: quien lo muestre pondrá su propio texto
const readError = (action) => {
    const message = action.error?.message

    return action.payload ?? (message === RTK_REJECTED_SENTINEL ? null : message) ?? null
}

const isLatest = (state, action) => state.requestId === action.meta.requestId

//Una lista filtrada se pide si no hay petición en vuelo y su copia no existe o ya venció
const isFilteredStale = (status, lastFetch) => {
    if (status === 'pending') return false
    if (!lastFetch) return true

    return Date.now() - lastFetch > COMPANIES_FILTERED_CACHE_TTL
}

export const getCompanies = createAsyncThunk('appCompanies/getCompanies', async (_, { rejectWithValue }) => {
    try {
        return {
            data: await companiesApi.fetchCompanies(),
            fetchedAt: Date.now()
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
}, {
    //Evita peticiones duplicadas y reutiliza el caché mientras siga vigente
    condition: (_, { getState }) => {
        const companies = getState()?.companies

        if (!companies) return true
        //Invalidación explícita (enableRequest): pasa aunque haya una petición en vuelo,
        //que pudo salir antes de la escritura; su respuesta se descarta por requestId
        if (companies.isRequested) return true
        //Ya hay una petición en vuelo: los demás se cuelgan de esa
        if (companies.status === 'pending') return false
        //Store vacío
        if (!companies.data?.length) return true

        return Date.now() - (companies.lastFetch ?? 0) > COMPANIES_CACHE_TTL
    }
})

export const getCompaniesToTracking = createAsyncThunk('appCompanies/getCompaniesToTracking', async (_, { rejectWithValue }) => {
    try {
        return {
            data: await companiesApi.fetchCompaniesTracking(),
            fetchedAt: Date.now()
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
}, {
    condition: (_, { getState }) => {
        const companies = getState()?.companies

        return !companies || isFilteredStale(companies.trackingStatus, companies.trackingLastFetch)
    }
})

export const getCompaniesTM = createAsyncThunk('appCompanies/getCompaniesTM', async (_, { rejectWithValue }) => {
    try {
        return {
            data: await companiesApi.fetchCompaniesTM(),
            fetchedAt: Date.now()
        }
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
}, {
    condition: (_, { getState }) => {
        const companies = getState()?.companies

        return !companies || isFilteredStale(companies.tmStatus, companies.tmLastFetch)
    }
})

export const appCompaniesSlice = createSlice({
    name: 'appCompanies',
    initialState: {
        data: [],
        isRequested: true,
        status: 'idle',
        error: null,
        lastFetch: null,
        requestId: null,
        dataCompaniesToTracking: [],
        trackingStatus: 'idle',
        trackingError: null,
        trackingLastFetch: null,
        trackingRequestId: null,
        dataCompaniesTM: [],
        tmStatus: 'idle',
        tmError: null,
        tmLastFetch: null,
        tmRequestId: null
    },
    reducers: {
        //Invalida también las listas filtradas: dependen de lo mismo que se acaba de escribir
        enableRequest: (state) => {
            state.isRequested = true
            state.trackingLastFetch = null
            state.tmLastFetch = null
        },
        //Vencen sólo su lista. A diferencia de enableRequest no se saltan la deduplicación de la
        //petición en vuelo: son lecturas, y una petición ya lanzada sirve igual
        invalidateCompaniesToTracking: (state) => {
            state.trackingLastFetch = null
        },
        invalidateCompaniesTM: (state) => {
            state.tmLastFetch = null
        }
    },
    extraReducers: builder => {
        builder.addCase(getCompanies.pending, (state, action) => {
            state.status = 'pending'
            state.error = null
            //Se consume aquí para que una invalidación durante la petición siga vigente
            state.isRequested = false
            state.requestId = action.meta.requestId
        })
        builder.addCase(getCompanies.fulfilled, (state, action) => {
            if (!isLatest(state, action)) return
            state.status = 'fulfilled'
            state.data = asList(action.payload.data)
            state.lastFetch = action.payload.fetchedAt
        })
        builder.addCase(getCompanies.rejected, (state, action) => {
            if (!isLatest(state, action)) return
            state.status = 'rejected'
            state.error = readError(action)
            //Permite reintentar en la siguiente llamada sin esperar al TTL
            state.isRequested = true
        })

        builder.addCase(getCompaniesToTracking.pending, (state, action) => {
            state.trackingStatus = 'pending'
            state.trackingError = null
            state.trackingRequestId = action.meta.requestId
        })
        builder.addCase(getCompaniesToTracking.fulfilled, (state, action) => {
            if (state.trackingRequestId !== action.meta.requestId) return
            state.trackingStatus = 'fulfilled'
            state.dataCompaniesToTracking = asList(action.payload.data)
            state.trackingLastFetch = action.payload.fetchedAt
        })
        builder.addCase(getCompaniesToTracking.rejected, (state, action) => {
            if (state.trackingRequestId !== action.meta.requestId) return
            state.trackingStatus = 'rejected'
            state.trackingError = readError(action)
            state.trackingLastFetch = null
        })

        builder.addCase(getCompaniesTM.pending, (state, action) => {
            state.tmStatus = 'pending'
            state.tmError = null
            state.tmRequestId = action.meta.requestId
        })
        builder.addCase(getCompaniesTM.fulfilled, (state, action) => {
            if (state.tmRequestId !== action.meta.requestId) return
            state.tmStatus = 'fulfilled'
            state.dataCompaniesTM = asList(action.payload.data)
            state.tmLastFetch = action.payload.fetchedAt
        })
        builder.addCase(getCompaniesTM.rejected, (state, action) => {
            if (state.tmRequestId !== action.meta.requestId) return
            state.tmStatus = 'rejected'
            state.tmError = readError(action)
            state.tmLastFetch = null
        })
    }
})

export const { enableRequest, invalidateCompaniesToTracking, invalidateCompaniesTM } = appCompaniesSlice.actions

export const updateCompany = createAsyncThunk('appCompanies/updateCompany', async (data, { dispatch, rejectWithValue }) => {
    try {
        const response = await companiesApi.updateCompany(data.rfc, data)
        dispatch(enableRequest())
        dispatch(getCompanies())
        return response
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const companiesLabels = createAsyncThunk('appCompanies/companiesLabels', async (data, { dispatch, rejectWithValue }) => {
    try {
        const response = await companiesApi.updateCompanyLabel(data.rfc, data)
        dispatch(enableRequest())
        dispatch(getCompanies())
        return response
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const updateStatus = createAsyncThunk('appCompanies/updateStatus', async (data, { dispatch, rejectWithValue }) => {
    try {
        const response = await companiesApi.updateCompanyStatus(data.rfc, data)
        dispatch(enableRequest())
        dispatch(getCompanies())
        return response
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export const companyDates = createAsyncThunk('appCompanies/companyDates', async (data, { dispatch, rejectWithValue }) => {
    try {
        const response = await companiesApi.updateCompanyStartEndDate(data.rfc, data)
        dispatch(enableRequest())
        dispatch(getCompanies())
        return response
    } catch (error) {
        return rejectWithValue(error.response?.data)
    }
})

export default appCompaniesSlice.reducer
